import { useEffect, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentUserContext } from '@/contexts';
import { useOrganizations } from '@/hooks/useOrganizations';
import { useQueryParams } from '@/hooks/useQueryParams';
import { useLastWorkspace } from './useLastWorkspace';

export const WORKSPACE_QUERY_PARAM = 'org';

export const getDashboardPath = (orgId?: string | null) =>
  orgId ? `/dashboard?${WORKSPACE_QUERY_PARAM}=${orgId}` : '/dashboard';

// Single home for the workspace-tab gating and naming shared by the tab row
// and the Dashboard's contextual section headings, so they can't drift.
// The selected workspace lives in the URL (?org=<id>, absent = personal), but
// the last-viewed workspace is also persisted so returning to the library from
// a surface that links to a bare /dashboard (e.g. Magic) reopens the user's
// tab instead of snapping back to personal.
export const useWorkspaceTabs = () => {
  const { t } = useTranslation('dashboard');
  const { currentUser } = useCurrentUserContext();
  const {
    sharedOrganizations,
    isLoading: organizationsLoading,
    isError: organizationsError,
    refetch: refetchOrganizations,
  } = useOrganizations();
  const { queryParams, setQueryParams, removeQueryParam } = useQueryParams();
  const { lastWorkspaceOrgId, setLastWorkspaceOrgId } = useLastWorkspace();

  const requestedOrgId = queryParams.get(WORKSPACE_QUERY_PARAM);

  // An id memberships positively rule out (left the org, someone else's link)
  // falls back to personal without rewriting the URL, mirroring useActiveView.
  const activeOrg =
    sharedOrganizations.find((org) => org.id === requestedOrgId) ?? null;
  const activeOrgId = activeOrg?.id ?? null;

  // A requested workspace is unresolved while memberships load — and stays
  // unresolved if the membership query fails. Consumers must gate
  // workspace-scoped reads and creates on this: falling back to personal
  // under an org URL would fetch the wrong library and home new experiences
  // in the wrong workspace.
  const isWorkspaceUnresolved =
    Boolean(requestedOrgId) &&
    !activeOrg &&
    (organizationsLoading || organizationsError);

  // The terminal subset of unresolved: memberships failed rather than still
  // loading. Surfaced by WorkspaceLoadError with retry / back-to-personal
  // actions so a failed org deep link doesn't strand the user on a skeleton.
  const isWorkspaceError =
    Boolean(requestedOrgId) && !activeOrg && organizationsError;

  // A lone personal tab carries no information — tabs only appear once
  // there's a second workspace to switch to.
  const showTabs = sharedOrganizations.length > 0;

  const personalWorkspaceName =
    currentUser?.name || currentUser?.email || t('workspace_tabs.personal');

  // The personal workspace is identified by the user's own avatar rather than a
  // generic icon; falls back to initials when there's no uploaded image.
  const personalWorkspaceAvatar = currentUser?.avatar || undefined;

  const activeWorkspaceName = activeOrg?.name ?? personalWorkspaceName;

  // Remember every explicit selection (including a switch back to personal, so
  // returning doesn't override that choice) as it happens — the restore below
  // reads this on the next bare /dashboard.
  const setActiveOrgId = (orgId: string | null) => {
    setLastWorkspaceOrgId(orgId);
    if (orgId) {
      setQueryParams({ [WORKSPACE_QUERY_PARAM]: orgId }, { replace: true });
    } else {
      removeQueryParam(WORKSPACE_QUERY_PARAM, { replace: true });
    }
  };

  // Also remember a deep link's workspace once it resolves, so returning to a
  // bare /dashboard reopens it too. Gated on an explicit ?org that resolved, so
  // it never writes personal over a remembered tab on a bare load.
  useEffect(() => {
    if (requestedOrgId && activeOrgId) {
      setLastWorkspaceOrgId(activeOrgId);
    }
  }, [requestedOrgId, activeOrgId, setLastWorkspaceOrgId]);

  // On arriving at a bare /dashboard, restore the last-viewed workspace once
  // memberships resolve — this is what brings the user back to their tab
  // instead of personal. An explicit ?org (deep link) and a personal selection
  // made this visit both leave param management to setActiveOrgId, so the
  // one-shot ref keeps this from fighting them. Runs in a layout effect so the
  // common cached-memberships case swaps to the remembered tab before paint,
  // and writes with replace so the restore stays out of history.
  const hasRestoredRef = useRef(false);
  useLayoutEffect(() => {
    // A failed membership query has nothing to restore from, so don't spend the
    // one-shot on it — a retry that succeeds can still reopen the remembered tab.
    if (hasRestoredRef.current || organizationsLoading || organizationsError) {
      return;
    }
    hasRestoredRef.current = true;

    const hasExplicitOrg = queryParams.has(WORKSPACE_QUERY_PARAM);
    const restorable = sharedOrganizations.find(
      (org) => org.id === lastWorkspaceOrgId
    );
    if (!hasExplicitOrg && restorable) {
      setActiveOrgId(restorable.id);
    }
  });

  return {
    showTabs,
    activeWorkspaceName,
    personalWorkspaceName,
    personalWorkspaceAvatar,
    sharedOrganizations,
    activeOrgId,
    isWorkspaceUnresolved,
    isWorkspaceError,
    retryWorkspaces: refetchOrganizations,
    setActiveOrgId,
  };
};
